import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { viewStory, deleteStory, getStoryViewers, likeStory, deleteHighlight } from "../../functions/story";
import { useSocketContext } from "../../context/SocketContext";
import "./style.css";
import { sendMessage } from "../../functions/message";
import axios from "axios";
import EmojiPicker from "emoji-picker-react";

export default function StoryViewer({
  userStories,
  currentIndex,
  onClose,
  onNext,
  onPrev,
  user,
  onStoryDeleted,
  isHighlight = false,
  isArchive = false,
  onArchiveDelete,
}) {
  const { user: storyUser, stories } = userStories || {};
  const currentStory = stories && Array.isArray(stories) && stories.length > 0 && currentIndex >= 0 && currentIndex < stories.length 
    ? stories[currentIndex] 
    : null;
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [segmentCount, setSegmentCount] = useState(1);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [viewCount, setViewCount] = useState(0);
  const [storyLikes, setStoryLikes] = useState({});
  const [showLikeBlast, setShowLikeBlast] = useState(false);
  const { socket } = useSocketContext();
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);
  const archiveMenuRef = useRef(null);
  const [showArchiveMenu, setShowArchiveMenu] = useState(false);
  const navigatingRef = useRef(false);
  const replyInputRef = useRef(null);
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [showStoryEmojiPicker, setShowStoryEmojiPicker] = useState(false);
  const isOwn = storyUser._id === user.id;
  const SEGMENT_DURATION = 10; // seconds
  // Add a ref to store allowed long videos (session) and use localStorage for persistence
  const allowedLongVideosRef = useRef({});
  const likeBlastTimeoutRef = useRef(null);

  // Helper to get/set allowed videos from localStorage
  const getAllowedLongVideos = () => {
    try {
      return JSON.parse(localStorage.getItem('allowedLongVideos') || '{}');
    } catch {
      return {};
    }
  };
  const setAllowedLongVideos = (obj) => {
    localStorage.setItem('allowedLongVideos', JSON.stringify(obj));
  };

  // Handle sending reply to story
  const handleSendReply = useCallback(async () => {
    if (!replyText.trim() || !storyUser?._id || !currentStory) return;
    
    try {
      // Pause the story while sending
      setIsPaused(true);
      
      // First, get or create a conversation with the story owner
      const { data: conversation } = await axios.post(
        '/api/messenger/conversations',
        { participantId: storyUser._id },
        { headers: { Authorization: `Bearer ${user.token}` }}
      );
      
      if (!conversation?._id) {
        throw new Error('Failed to get or create conversation');
      }
      
      // Get the story preview data
      const storyPreview = {
        id: currentStory._id,
        type: currentStory.image ? 'image' : currentStory.video ? 'video' : 'text',
        url: currentStory.image || currentStory.video || null,
        text: currentStory.text || '',
        previewUrl: currentStory.image || currentStory.video || null,
        timestamp: new Date().toISOString()
      };
      
      // Then send the message with the conversation ID and story context
      await sendMessage({
        conversationId: conversation._id,
        text: replyText,
        story: storyPreview,
        token: user.token
      });
      
      // Reset the reply state and resume the story
      setReplyText("");
      setIsReplying(false);
      setShowStoryEmojiPicker(false);
      setIsPaused(false);
      
    } catch (error) {
      console.error("Error sending reply:", error);
      // Resume the story even if there's an error
      setIsPaused(false);
      
      // Show error to user
      alert('Failed to send reply. Please try again.');
    }
  }, [replyText, storyUser, currentStory, user.token]);

  // Handle key press for reply input
  const handleReplyKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  }, [handleSendReply]);

  const handleStoryEmojiClick = useCallback((event, emojiObject) => {
    setIsPaused(true);
    setReplyText((prev) => prev + emojiObject.emoji);
    replyInputRef.current?.focus();
  }, []);

  // Toggle reply input is handled inline where needed

  // Navigation guard helper
  const navigateNext = useCallback((targetIndex) => {
    if (navigatingRef.current) return;
    navigatingRef.current = true;
    setTimeout(() => { navigatingRef.current = false; }, 400);

    // If explicitly targeting an index, honor it.
    if (typeof targetIndex === 'number' && typeof onNext === 'function') {
      onNext(targetIndex);
      return;
    }

    // Own stories: advance only within this user's list; do not jump to recipients automatically
    if (isOwn) {
      if (currentIndex < stories.length - 1) {
        onNext && onNext(currentIndex + 1);
      } else {
        // Reached the end of own stories, simply close
        onClose && onClose();
      }
      return;
    }

    // Recipient stories: use default parent behavior
    if (typeof onNext === 'function') onNext();
  }, [onNext, onClose, isOwn, currentIndex, stories.length]);
  // Note: navigatePrev was removed to avoid unused variable warning

  // Load persisted likes from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem("storyLikes");
      if (raw) {
        setStoryLikes(JSON.parse(raw));
      }
    } catch {
      // ignore storage read errors
    }
  }, []);

  useEffect(() => {
    return () => {
      if (likeBlastTimeoutRef.current) {
        clearTimeout(likeBlastTimeoutRef.current);
      }
    };
  }, []);

  // Archive/full-screen viewer: prevent page scroll and navbar overlap behind overlay
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (isArchive) {
      document.body.classList.add("story-viewer-archive-open");
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove("story-viewer-archive-open");
    };
  }, [isArchive]);

  useEffect(() => {
    // Mark story as viewed
    if (!isOwn && currentStory && currentStory._id) {
      viewStory(currentStory._id, user.token);
    }
  }, [currentStory, user.token, isOwn]);

  useEffect(() => {
    // Set initial view count
    if (currentStory?.viewers) {
      setViewCount(currentStory.viewers.length);
    }
  }, [currentStory]);

  const handleToggleLike = async () => {
    if (!currentStory?._id || isOwn) return;
    const storyId = currentStory._id;
    const userId = user.id || user._id;
    const wasLiked = !!(storyLikes[storyId]?.[userId]);
    const isLiking = !wasLiked;

    setStoryLikes((prev) => {
      const next = { ...prev };
      const existing = next[storyId] ? { ...next[storyId] } : {};
      if (existing[userId]) {
        delete existing[userId];
      } else {
        existing[userId] = true;
      }
      if (Object.keys(existing).length === 0) {
        delete next[storyId];
      } else {
        next[storyId] = existing;
      }
      try {
        localStorage.setItem("storyLikes", JSON.stringify(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });

    // Trigger like blast only when turning like on
    if (isLiking) {
      setShowLikeBlast(true);
      if (likeBlastTimeoutRef.current) {
        clearTimeout(likeBlastTimeoutRef.current);
      }
      likeBlastTimeoutRef.current = setTimeout(() => {
        setShowLikeBlast(false);
      }, 900);

      // When liking, add user to viewers list if not already there (like WhatsApp)
      // Check if user has already viewed this story
      const hasViewed = currentStory?.viewers?.some(
        (viewer) => viewer.user?._id === userId || viewer.user?._id?.toString() === userId?.toString()
      );

      if (!hasViewed) {
        try {
          // Call viewStory to add user to viewers list
          await viewStory(storyId, user.token);
          // The socket event will update the viewers list in real-time
        } catch (error) {
          console.error("Error adding viewer when liking:", error);
        }
      }
    }

    // Call backend API to like/unlike the story
    try {
      await likeStory(storyId, user.token);
      // If viewing viewers list, refresh it to show updated like status
      if (showViewers) {
        const response = await getStoryViewers(currentStory._id, user.token);
        setViewers(response.viewers || []);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      // Revert optimistic update on error
      setStoryLikes((prev) => {
        const next = { ...prev };
        const existing = next[storyId] ? { ...next[storyId] } : {};
        if (wasLiked) {
          existing[userId] = true;
        } else {
          delete existing[userId];
        }
        if (Object.keys(existing).length === 0) {
          delete next[storyId];
        } else {
          next[storyId] = existing;
        }
        try {
          localStorage.setItem("storyLikes", JSON.stringify(next));
        } catch {}
        return next;
      });
    }
  };

  const isCurrentStoryLiked = currentStory?._id
    ? !!(storyLikes[currentStory._id]?.[user.id || user._id])
    : false;

  const storyOwnerName = isOwn
    ? "My vibe"
    : storyUser?.first_name || storyUser?.username || storyUser?.name || "Story";

  const storyTime = (() => {
    const createdAt = currentStory?.createdAt || currentStory?.created_at;
    if (!createdAt) return "";
    const diffMs = Date.now() - new Date(createdAt).getTime();
    if (Number.isNaN(diffMs) || diffMs < 0) return "";
    const mins = Math.floor(diffMs / (1000 * 60));
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  })();

  useEffect(() => {
    // Reset segment info on story change
    setSegmentIndex(0);
    setProgress(0);
    setIsPaused(false);
    setShowMenu(false);
    setShowViewers(false);
    if (currentStory && currentStory.video) {
      // Try to get video duration
      const isBlob = currentStory.video.startsWith('blob:');
      const videoSrc = isBlob ? currentStory.video : `/uploads/stories/${currentStory.video}`;
      const video = document.createElement('video');
      video.src = videoSrc;
      video.onloadedmetadata = () => {
        const duration = video.duration || 10;
        // Show a single continuous progress bar for videos up to 60 seconds.
        // For longer videos, break into 30s segments.
        if (duration <= 60) {
          setSegmentCount(1);
        } else {
          setSegmentCount(Math.ceil(duration / 30));
        }
      };
      // Fallback if metadata doesn't load
      setTimeout(() => {
        if (!video.duration) setSegmentCount(1);
      }, 1000);
    } else {
      setSegmentCount(1);
    }
  }, [currentIndex, currentStory]);

  // Preload next story's image to avoid delay when advancing from a video
  useEffect(() => {
    const nextItem = stories[currentIndex + 1];
    if (nextItem && typeof nextItem.image === 'string' && !nextItem.image.startsWith('blob:')) {
      const img = new Image();
      img.src = `/uploads/stories/${nextItem.image}`;
    }
  }, [stories, currentIndex]);

  // Handle real-time view updates
  useEffect(() => {
    if (!socket || !currentStory?._id) return;

    const handleStoryViewed = (data) => {
      if (data.storyId === currentStory._id) {
        setViewCount(data.viewCount);
        
        // Update viewers list if it's open
        if (showViewers) {
          setViewers(prevViewers => {
            // Check if viewer already exists in the list
            const viewerExists = prevViewers.some(
              viewer => viewer.user._id === data.viewer._id
            );
            
            if (!viewerExists) {
              return [{
                user: data.viewer,
                viewedAt: data.viewedAt
              }, ...prevViewers];
            }
            return prevViewers;
          });
        }
      }
    };

    socket.on('story_viewed', handleStoryViewed);
    return () => {
      socket.off('story_viewed', handleStoryViewed);
    };
  }, [socket, currentStory?._id, showViewers]);

  // Handle real-time story like updates (for story owner)
  useEffect(() => {
    if (!socket || !currentStory?._id || !isOwn) return;

    const handleStoryLiked = async (data) => {
      if (data.storyId === currentStory._id) {
        // Refresh the viewers list to get updated like information
        try {
          const response = await getStoryViewers(currentStory._id, user.token);
          if (response.viewers) {
            setViewers(response.viewers || []);
          }
        } catch (error) {
          console.error("Error refreshing viewers list after like:", error);
        }
      }
    };

    socket.on('story_liked', handleStoryLiked);
    return () => {
      socket.off('story_liked', handleStoryLiked);
    };
  }, [socket, currentStory?._id, isOwn, user.token, showViewers]);

  // Close menu on outside click or Escape, and pause while open
  useEffect(() => {
    if (!showMenu) return;
    const handleDocClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          menuBtnRef.current && !menuBtnRef.current.contains(event.target) &&
          (!replyInputRef.current || !replyInputRef.current.contains(event.target))) {
        setShowMenu(false);
        if (isReplying) {
          setIsReplying(false);
          setIsPaused(false);
        }
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setShowMenu(false);
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showMenu, isReplying]);

  // Close archive menu on outside click or Escape
  useEffect(() => {
    if (!showArchiveMenu) return;
    const handleDocClick = (event) => {
      if (archiveMenuRef.current && !archiveMenuRef.current.contains(event.target)) {
        setShowArchiveMenu(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setShowArchiveMenu(false);
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showArchiveMenu]);

  useEffect(() => {
    // Pause story progress and video when an overlay opens; resume when it closes
    const video = document.querySelector('.story_viewer_video');
    if (showMenu || showArchiveMenu || showViewers) {
      setIsPaused(true);
      if (video) video.pause();
    } else {
      setIsPaused(false);
      if (video) video.play();
    }
  }, [showMenu, showArchiveMenu, showViewers]);

  // Reset progress ONLY when story or segment changes, not when pausing/resuming
  useEffect(() => {
    setProgress(0);
  }, [currentIndex, currentStory, segmentIndex, segmentCount]);

  // Drive progress timer; do not reset when toggling pause/resume
  useEffect(() => {
    if (isPaused) return;
    // If this story is a video shown with a single continuous progress bar,
    // rely on the video's timeupdate event to update progress so the bar
    // exactly matches playback. Skip the JS interval timer in that case.
    if (currentStory && currentStory.video && segmentCount === 1) {
      return;
    }
    let duration = 10;
    let segmentLength = SEGMENT_DURATION;
    if (currentStory && currentStory.video && segmentCount > 1) {
      // If video is longer than 30s, use 30s segments
      segmentLength = (currentStory && currentStory.video && segmentCount > 1) ? 30 : SEGMENT_DURATION;
      duration = segmentLength;
    } else if (currentStory && currentStory.image) {
      duration = 5; // Set image duration to 5 seconds
    }
    const intervalMs = 50;
    const increment = 100 / ((duration * 1000) / intervalMs);
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          return 100;
        }
        return prev + increment;
      });
    }, intervalMs);
    return () => clearInterval(timer);
  }, [isPaused, currentStory, segmentIndex, segmentCount, currentIndex]);

  // Move to next segment or next story when progress reaches 100
  useEffect(() => {
    if (progress >= 100) {
      // For videos: only advance segments here; do NOT navigate to next story.
      if (currentStory && currentStory.video) {
        if (segmentIndex < segmentCount - 1) {
          setSegmentIndex(segmentIndex + 1);
          setProgress(0);
        }
        return;
      }
      // For images/text: navigate to next story when finished
      setSegmentIndex(0);
      navigateNext();
    }
  }, [progress, currentStory, segmentIndex, segmentCount, navigateNext]);

  // Check for long video and show custom popup message if needed, remove if not allowed
  const [showLongVideoWarning, setShowLongVideoWarning] = useState(false);
  const [allowLongVideo, setAllowLongVideo] = useState(null); // null: not chosen, true: allow, false: only 30s

  
  useEffect(() => {
    // Load allowed videos from localStorage on mount
    allowedLongVideosRef.current = getAllowedLongVideos();
  }, []);

  useEffect(() => {
    // If this video was already allowed, skip asking again
    if (currentStory && currentStory.video) {
      const videoKey = currentStory.video;
      if (allowedLongVideosRef.current[videoKey]) {
        setAllowLongVideo(true);
        setShowLongVideoWarning(false);
        return;
      }
    }
    setAllowLongVideo(null); // reset on story change
    if (currentStory && currentStory.video) {
      const isBlob = currentStory.video.startsWith('blob:');
      const videoSrc = isBlob ? currentStory.video : `/uploads/stories/${currentStory.video}`;
      const video = document.createElement('video');
      video.src = videoSrc;
      video.onloadedmetadata = () => {
        const duration = video.duration || 10;
        // Only warn for videos longer than 60 seconds
        if (duration > 60) {
          setShowLongVideoWarning(true);
        } else {
          setShowLongVideoWarning(false);
        }
      };
    } else {
      setShowLongVideoWarning(false);
    }
  }, [currentStory]);

  const handleLongVideoPermission = (allow) => {
    setShowLongVideoWarning(false);
    setAllowLongVideo(allow);
    if (currentStory && currentStory.video) {
      // Remember this video as handled (allowed or cancelled)
      allowedLongVideosRef.current[currentStory.video] = allow ? true : 'cancelled';
      setAllowedLongVideos(allowedLongVideosRef.current);
    }
    if (!allow) {
      setSegmentIndex(0);
      setProgress(0);
    }
  };

  useEffect(() => {
    // If this video was already handled, set allowLongVideo accordingly
    if (currentStory && currentStory.video) {
      const videoKey = currentStory.video;
      const allowed = allowedLongVideosRef.current[videoKey];
      if (allowed === true) {
        setAllowLongVideo(true);
        setShowLongVideoWarning(false);
        return;
      } else if (allowed === 'cancelled') {
        setAllowLongVideo(false);
        setShowLongVideoWarning(false);
        return;
      }
    }
    setAllowLongVideo(null); // reset on story change
    if (currentStory && currentStory.video) {
      const isBlob = currentStory.video.startsWith('blob:');
      const videoSrc = isBlob ? currentStory.video : `/uploads/stories/${currentStory.video}`;
      const video = document.createElement('video');
      video.src = videoSrc;
      video.onloadedmetadata = () => {
        const duration = video.duration || 10;
        // Only warn for videos longer than 60 seconds
        if (duration > 60) {
          setShowLongVideoWarning(true);
        } else {
          setShowLongVideoWarning(false);
        }
      };
    } else {
      setShowLongVideoWarning(false);
    }
  }, [currentStory]);

  const handlePause = () => setIsPaused(true);
  const handleResume = () => setIsPaused(false);

  const handleDelete = async () => {
    setShowDeleteConfirm(true);
    setShowMenu(false);
  };

  const confirmDelete = async () => {
    if (!currentStory) return;
    try {
      if (isHighlight) {
        // Use deleteHighlight for highlights
        await deleteHighlight(currentStory._id, user.token);
      } else {
        // Use deleteStory for regular stories
        await deleteStory(currentStory._id, user.token);
      }
      setShowDeleteConfirm(false);
      // Refresh stories list in parent component before navigating
      // This ensures deleted story is removed from the list immediately
      if (onStoryDeleted) {
        await onStoryDeleted();
      }
      onNext(); // Move to next story after deletion
    } catch (error) {
      console.error("Error deleting story:", error);
      setShowDeleteConfirm(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleViewViewers = async () => {
    if (!currentStory) return;
    if (isOwn) {
      try {
        const response = await getStoryViewers(currentStory._id, user.token);
        setViewers(response.viewers || []);
        setShowViewers(true);
      } catch (error) {
        console.error("Error fetching viewers:", error);
      }
    }
  };

  // Play/pause toggle for video only
  const handleTogglePause = () => {
    // Toggle pause for all story types. If a video exists, sync the element state too.
    setIsPaused((prev) => {
      const next = !prev;
      const video = document.querySelector('.story_viewer_video');
      if (video) {
        if (next) {
          video.pause();
        } else {
          video.play();
        }
      }
      return next;
    });
  };

  const getStoryContent = () => {
    // Safety check: ensure currentStory exists
    if (!currentStory) {
      return null;
    }
    
    if (currentStory.image) {
      // Display image if present (support both blob and server URLs)
      const isBlob = currentStory.image.startsWith('blob:');
      const imageSrc = isBlob ? currentStory.image : `/uploads/stories/${currentStory.image}`;
      return (
        <div className="story_viewer_media_text" style={{ position: 'relative', width: '100%', height: '100%' }} onClick={handleTogglePause}>
          <img
            src={imageSrc}
            alt="Story"
            className="story_viewer_image"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
          {currentStory.text && (
            <div
              className="story_viewer_text"
              style={{
                backgroundColor: 'rgba(0,0,0,0.4)',
                fontFamily: currentStory.font || "Arial",
                position: "absolute",
                bottom: !isOwn ? 100 : 30,
                left: 0,
                width: "100%",
                color: "#fff",
                textAlign: "center",
                padding: 8,
                fontSize: 22,
                textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                zIndex: 1
              }}
            >
              {currentStory.text}
            </div>
          )}
        </div>
      );
    } else if (currentStory.video) {
      const isBlob = currentStory.video.startsWith('blob:');
      const videoSrc = isBlob ? currentStory.video : `/uploads/stories/${currentStory.video}`;
      const videoProps = allowLongVideo === false ? { max: 30 } : {};
      // Only render video if permission is chosen (true or false)
      if (allowLongVideo === null && showLongVideoWarning) {
        // Don't render video while permission popup is shown
        return null;
      }
      return (
        <div className="story_viewer_media_text" style={{ position: 'relative', width: '100%', height: '100%' }} onClick={handleTogglePause}>
          <video
            src={videoSrc}
            className="story_viewer_video"
            autoPlay
            playsInline
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            onContextMenu={(e) => e.preventDefault()}
            onPause={handlePause}
            onPlay={handleResume}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            onEnded={() => {
              // When video finishes, automatically show the next story in this user's list
              navigateNext();
            }}
            onTimeUpdate={e => {
              if (allowLongVideo === false && e.target.currentTime >= 30) {
                e.target.onTimeUpdate = null; // Prevent multiple triggers
                navigateNext();
                return;
              }
              // If this video uses a single continuous progress bar, compute
              // progress from the actual currentTime/duration so the bar
              // reaches the end exactly when the video ends.
              if (segmentCount === 1) {
                const dur = e.target.duration || 1;
                const pct = (e.target.currentTime / dur) * 100;
                setProgress(Math.min(100, pct));
              }
            }}
            {...videoProps}
          />
          {currentStory.text && (
            <div
              className="story_viewer_text"
              style={{
                backgroundColor: 'rgba(0,0,0,0.4)',
                fontFamily: currentStory.font || "Arial",
                position: "absolute",
                bottom: !isOwn ? 100 : 30,
                left: 0,
                width: "100%",
                color: "#fff",
                textAlign: "center",
                padding: 8,
                fontSize: 22,
                textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                zIndex: 1
              }}
            >
              {currentStory.text}
            </div>
          )}
        </div>
      );
    } else if (currentStory.text) {
      return (
        <div
          className="story_viewer_text"
          style={{
            backgroundColor: currentStory.backgroundColor || "#1876f2",
            fontFamily: currentStory.font || "Arial",
            width: '100%',
            height: '100%',
            minHeight: '70vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            color: '#fff',
            textAlign: 'center',
            padding: 16
          }}
          onClick={handleTogglePause}
        >
          {currentStory.text}
        </div>
      );
    } else {
      return null;
    }
  };

  // Early return if no current story
  if (!currentStory) {
    return null;
  }

  // Portal to body so fixed overlay is not trapped under profile_top
  // (mobile profile_top z-index:100 vs profile_bottom z-index:1 stacking).
  const viewer = (
    <div className={`story_viewer_overlay${isArchive ? " is-archive" : ""}`}>
      <div className={`story_viewer_content${isArchive ? " is-archive" : ""}`}>
        {/* Navigation */}
        {stories.length > 1 && currentIndex > 0 && (
          <button className="story_nav_btn left" onClick={() => {
            setProgress(0);
            setSegmentIndex(0);
            if (isOwn) {
              if (typeof onPrev === 'function' && currentIndex > 0) onPrev(currentIndex - 1);
            } else {
              if (typeof onPrev === 'function') onPrev();
            }
          }}>
            ‹
          </button>
        )}
        {stories.length > 1 && currentIndex < stories.length - 1 && (
          <button className="story_nav_btn right" onClick={() => {
            setProgress(0);
            setSegmentIndex(0);
            if (isOwn) {
              if (typeof onNext === 'function') {
                if (currentIndex < stories.length - 1) onNext(currentIndex + 1);
                else onClose && onClose();
              }
            } else {
              if (typeof onNext === 'function') onNext();
            }
          }}>
            ›
          </button>
        )}
        {/* Top bar with profile image and progress bar */}
        <div className="story_viewer_topbar">
          <div className="story_viewer_topbar_progress_row">
            {/* WhatsApp-style progress bar for all stories and highlights */}
            <div
              className="story_viewer_progress_container"
              style={{
                flex: 1,
                height: 4,
                background: "rgba(128,128,128,0.3)",
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                gap: 2,
              }}
            >
              {(
                currentStory && currentStory.video && segmentCount > 1 && allowLongVideo !== false
                  ? [...Array(segmentCount)]
                  : [...Array(stories.length)]
              ).map((_, idx) => (
                <div
                  key={idx}
                  className="story_viewer_progress"
                  style={{
                    flex: 1,
                    height: "100%",
                    background: (
                      currentStory && currentStory.video && segmentCount > 1 && allowLongVideo !== false
                    )
                      ? (idx < segmentIndex
                          ? "#fff"
                          : idx === segmentIndex
                          ? `linear-gradient(to right, #fff ${progress}%, rgba(128,128,128,0.3) ${progress}%)`
                          : "rgba(128,128,128,0.3)")
                      : (idx < currentIndex
                          ? "#fff"
                          : idx === currentIndex
                          ? `linear-gradient(to right, #fff ${progress}%, rgba(128,128,128,0.3) ${progress}%)`
                          : "rgba(128,128,128,0.3)"),
                    transition: isPaused ? "none" : "width 0.05s linear",
                    marginRight: idx < (
                      currentStory && currentStory.video && segmentCount > 1 && allowLongVideo !== false
                        ? segmentCount
                        : stories.length
                    ) - 1 ? 2 : 0,
                    borderRadius: 3,
                    cursor: (
                      currentStory && currentStory.video && segmentCount > 1 && allowLongVideo !== false
                        ? segmentCount
                        : stories.length
                    ) > 1 ? "pointer" : "default",
                  }}
                  onClick={() => {
                    if (currentStory && currentStory.video && segmentCount > 1 && allowLongVideo !== false) {
                      setSegmentIndex(idx);
                      setProgress(0);
                      const video = document.querySelector(".story_viewer_video");
                      if (video) {
                        video.currentTime = idx * 30;
                        video.play();
                      }
                    } else if (stories.length > 1 && idx !== currentIndex) {
                      setSegmentIndex(0);
                      setProgress(0);
                      onPrev && idx < currentIndex && onPrev(idx);
                      onNext && idx > currentIndex && onNext(idx);
                    }
                  }}
                >
                  {(
                    currentStory && currentStory.video && segmentCount > 1 && allowLongVideo !== false
                      ? idx === segmentIndex
                      : idx === currentIndex
                  ) ? (
                    <div style={{ width: `${progress}%`, height: "100%", background: "#fff", borderRadius: 3 }}></div>
                  ) : null}
                </div>
              ))}
            </div>
            <button
              className="story_close_btn story_topbar_close_btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof onClose === "function") {
                  onClose();
                }
              }}
              aria-label="Close"
              type="button"
            >
              ×
            </button>
          </div>
          <div className="story_viewer_user_row">
            <div className="story_viewer_user_identity">
              <img
                src={storyUser.picture || "/images/default_profile.png"}
                alt="Profile"
                className="story_viewer_profile_img"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid #fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              />
              <div className="story_viewer_username">
                <span>{storyOwnerName}</span>
                {storyTime ? <span>{storyTime}</span> : null}
              </div>
            </div>
            {isOwn && !isArchive && (
              <div className="story_topbar_menu_wrapper">
                <button
                  className="story_menu_btn story_topbar_menu_btn"
                  aria-label="Story options"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu((v) => !v);
                  }}
                  ref={menuBtnRef}
                >
                  ⋮
                </button>
                {showMenu && (
                  <div
                    className="story_menu_dropdown story_topbar_menu_dropdown"
                    onClick={(e) => e.stopPropagation()}
                    ref={menuRef}
                  >
                    <button className="story_menu_item delete" onClick={handleDelete}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
            {isArchive && typeof onArchiveDelete === "function" && (
              <div className="story_topbar_menu_wrapper" ref={archiveMenuRef}>
                <button
                  type="button"
                  className="story_menu_btn story_topbar_menu_btn notranslate"
                  aria-label="Archive options"
                  translate="no"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowArchiveMenu((v) => !v);
                  }}
                >
                  <span className="notranslate">⋮</span>
                </button>
                {showArchiveMenu && (
                  <div className="story_menu_dropdown story_topbar_menu_dropdown" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="story_menu_item delete"
                      onClick={() => {
                        onArchiveDelete(currentStory);
                        setShowArchiveMenu(false);
                      }}
                    >
                      Delete from Archive
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Story Content */}
        <div className="story_viewer_body">{getStoryContent()}</div>
        {/* Reply UI */}
        {isReplying && (
          <div className="story-reply-container">
            {showStoryEmojiPicker && (
              <div className="story-reply-emoji-picker" onClick={(e) => e.stopPropagation()}>
                <EmojiPicker
                  onEmojiClick={handleStoryEmojiClick}
                  height={320}
                  width="100%"
                  theme="auto"
                  disableSkinTonePicker={true}
                />
              </div>
            )}
            <div className="story-reply-input-container">
              <input
                type="text"
                ref={replyInputRef}
                value={replyText}
                onChange={(e) => {
                  setReplyText(e.target.value);
                  if (e.target.value.trim() !== '') {
                    setIsPaused(true);
                  }
                }}
                onKeyPress={handleReplyKeyPress}
                placeholder="Send a reply..."
                className="story-reply-input"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
              <button
                type="button"
                className="story-reply-emoji-sender"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPaused(true);
                  setShowStoryEmojiPicker((v) => !v);
                }}
                title="Add emoji"
                aria-label="Add emoji"
              >
                <span className="story-reply-emoji-icon">😊</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendReply();
                }}
                className="story-reply-send-btn"
                disabled={!replyText.trim()}
                title="Send"
                aria-label="Send reply"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Reply input - always visible for non-own stories */}
        {!isOwn && (
          <>
            <div className="story-reply-dock">
              <div className="story-reply-container">
              {showStoryEmojiPicker && (
                <div className="story-reply-emoji-picker" onClick={(e) => e.stopPropagation()}>
                  <EmojiPicker
                    onEmojiClick={handleStoryEmojiClick}
                    height={320}
                    width="100%"
                    theme="auto"
                    disableSkinTonePicker={true}
                  />
                </div>
              )}
              <div className="story-reply-input-container">
                <input
                  type="text"
                  ref={replyInputRef}
                  value={replyText}
                  onChange={(e) => {
                    setReplyText(e.target.value);
                    if (e.target.value.trim() !== '') {
                      setIsPaused(true);
                    }
                  }}
                  onKeyPress={handleReplyKeyPress}
                  placeholder="Send a reply..."
                  className="story-reply-input"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  className="story-reply-emoji-sender"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPaused(true);
                    setShowStoryEmojiPicker((v) => !v);
                  }}
                  title="Add emoji"
                  aria-label="Add emoji"
                >
                  <span className="story-reply-emoji-icon">😊</span>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendReply();
                  }}
                  className="story-reply-send-btn"
                  disabled={!replyText.trim()}
                  title="Send"
                  aria-label="Send reply"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>
              </div>
              <button
              type="button"
              className={`story_like_btn story_like_btn--dock ${isCurrentStoryLiked ? 'liked' : ''}`}
              aria-pressed={isCurrentStoryLiked ? 'true' : 'false'}
              aria-label="Like story"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleLike();
              }}
            >
              <span className="story_like_heart notranslate">❤</span>
            </button>
            </div>
            {showLikeBlast && (
              <div className="story_like_blast">
                <span className="notranslate">❤</span>
              </div>
            )}
          </>
        )}

        {/* Story viewers count and view button - Hide for highlights and archive */}
        {isOwn && !isHighlight && !isArchive && (
          <div 
            className="story_viewers_count" 
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '14px',
              background: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              padding: '4px 9px',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              zIndex: 2,
              fontSize: '12px',
              backdropFilter: 'blur(3px)',
              transition: 'background 0.2s ease',
              ':hover': {
                background: 'rgba(0, 0, 0, 0.6)',
              },
            }}
            onClick={handleViewViewers}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
            }}>
              <svg 
                viewBox="0 0 24 24" 
                width="14" 
                height="14" 
                fill="#fff"
                style={{
                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))',
                }}
              >
                <path d="M12 5c-7.633 0-9.927 6.617-9.948 6.684-.122.305-.12.64.005.942C2.073 12.383 4.367 19 12 19s9.927-6.617 9.948-6.684c.122-.305.12-.64-.005-.942C21.927 6.617 19.633 5 12 5zm0 12c-5.351 0-7.424-3.846-7.926-5C4.578 10.842 6.652 7 12 7c5.351 0 7.424 3.846 7.926 5-.504 1.158-2.578 5-7.926 5z"/>
                <path d="M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
              </svg>
            </div>
            <span style={{
              fontSize: '12px',
              fontWeight: '500',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            }}>
              {viewCount}
            </span>
          </div>
        )}
        {/* Viewers List - Responsive Design */}
        {showViewers && (
          <div className="story_viewers_modal">
            <div className="story_viewers_modal_content">
              {/* Header */}
              <div className="story_viewers_header">
                <h3>Viewers</h3>
                <button 
                  onClick={() => setShowViewers(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="var(--color-secondary)">
                    <path d="M19.6 17.2l-6.2-6.7 6.2-6.7-1.4-1.4-6.2 6.7-6.2-6.7-1.4 1.4 6.2 6.7-6.2 6.7 1.4 1.4 6.2-6.7 6.2 6.7z"/>
                  </svg>
                </button>
              </div>

              {/* Viewers Count */}
              <div className="story_viewers_count_section">
                {viewCount} {viewCount === 1 ? 'viewer' : 'viewers'}
              </div>

              {/* Viewers List */}
              <div className="story_viewers_list">
                {viewers.length > 0 ? (
                  viewers.map((viewer, index) => {
                    const viewedAt = new Date(viewer.viewedAt);
                    const now = new Date();
                    const diffHours = Math.floor((now - viewedAt) / (1000 * 60 * 60));
                    let timeAgo = '';
                    
                    if (diffHours < 1) {
                      const diffMins = Math.floor((now - viewedAt) / (1000 * 60));
                      timeAgo = diffMins <= 1 ? 'just now' : `${diffMins} min ago`;
                    } else if (diffHours < 24) {
                      timeAgo = `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
                    } else {
                      timeAgo = viewedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }
                    
                    return (
                      <div 
                        key={index}
                        className="story_viewer_item"
                      >
                        <img 
                          src={viewer.user?.picture || '/images/default_profile.png'}
                          alt={viewer.user?.first_name}
                        />
                        <div style={{
                          flex: 1,
                          minWidth: 0,
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '8px',
                          }}>
                            <span className="story_viewer_item_name">
                              {viewer.user?.first_name || viewer.user?.username || viewer.user?.name || "Unknown"}
                            </span>
                            {viewer.liked && (
                              <span className="viewer_like_badge">
                                ❤
                              </span>
                            )}
                            <span className="viewer_time">
                              {timeAgo}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    padding: '30px 20px',
                    textAlign: 'center',
                    color: 'var(--color-secondary)',
                    fontSize: '14px',
                  }}>
                    No viewers yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Custom popup for long video warning */}
      {showLongVideoWarning && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',padding:24,borderRadius:8,maxWidth:350,textAlign:'center',boxShadow:'0 2px 16px rgba(0,0,0,0.2)'}}>
            <div style={{fontWeight:'bold',fontSize:18,marginBottom:12}}>Long Video Detected</div>
            <div style={{marginBottom:20}}>This video is longer than 30 seconds. Do you want to allow uploading and viewing as multiple segments?</div>
            <button style={{marginRight:12,padding:'6px 18px',background:'#1876f2',color:'#fff',border:'none',borderRadius:4,cursor:'pointer'}} onClick={()=>handleLongVideoPermission(true)}>Allow</button>
            <button style={{padding:'6px 18px',background:'#eee',color:'#333',border:'none',borderRadius:4,cursor:'pointer'}} onClick={()=>handleLongVideoPermission(false)}>Cancel</button>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--bg-primary)',
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '90%',
            width: '400px',
            textAlign: 'center',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
          }}>
            <p style={{
              marginBottom: '20px',
              color: 'var(--color-primary)',
              fontSize: '16px'
            }}>{isHighlight ? "Are you sure you want to delete these memories?" : "Are you sure you want to delete this vibe?"}</p>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '15px'
            }}>
              <button 
                onClick={cancelDelete}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  background: 'var(--bg-third)',
                  color: 'var(--color-primary)',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  background: '#f44336',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(viewer, document.body);
}
